import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";
import Slider from "@mui/material/Slider";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";

import { sizeChartOptions, seasonOptions } from "utils/data";
import { DatePicker } from "antd";

const sizeChartGenderOptions = sizeChartOptions.map((x) => ({ label: x.label, value: x.label }));
const unitOptions = [
  {
    label: "inch",
    value: "inch",
  },
  {
    label: "cm",
    value: "cm",
  },
];

const AdvancedSettingModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      jobconf = {},
      setJobconf,
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
    },
    ref
  ) => {
    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();

    useEffect(() => {
      reset({
        negative_prompt: jobconf?.negative_prompt,
        inference_steps: jobconf?.max_num_inference_steps,
        control_level: jobconf?.conditioning_scale,
        imagination_level: jobconf?.guidance_scale,
      });
    }, [jobconf]);

    const handleSaveSetting = (data) => {
      console.log(data);
      const payload = {
        ...jobconf,
        negative_prompt: data["negative_prompt"] ? data["negative_prompt"] : "",
        max_num_inference_steps: data["inference_steps"],
        conditioning_scale: data["control_level"],
        guidance_scale: data["imagination_level"],
      };
      setJobconf(payload);
      handleClose();
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title">
          {"Advanced Setting"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleSaveSetting)}
          style={{ overflow: "auto", maxHeight: "600px" }}
        >
          <DialogContent>
            <SuiBox px={2}>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Inference Steps:
                  </SuiTypography>
                </SuiBox>
                <SuiBox mx={1.5}>
                  <Controller
                    name="inference_steps"
                    control={control}
                    render={({ field }) => (
                      <Slider {...field} min={15} max={20} step={1} valueLabelDisplay="auto" />
                    )}
                  />
                </SuiBox>
              </SuiBox>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Imagination Level:
                  </SuiTypography>
                </SuiBox>
                <SuiBox mx={1.5}>
                  <Controller
                    name="imagination_level"
                    control={control}
                    render={({ field }) => (
                      <Slider {...field} min={4} max={10} step={0.5} valueLabelDisplay="auto" />
                    )}
                  />
                </SuiBox>
              </SuiBox>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Control Level:
                  </SuiTypography>
                </SuiBox>
                <SuiBox mx={1.5}>
                  <Controller
                    name="control_level"
                    control={control}
                    render={({ field }) => (
                      <Slider {...field} min={0.5} max={2} step={0.1} valueLabelDisplay="auto" />
                    )}
                  />
                </SuiBox>
              </SuiBox>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Negative Prompt:
                  </SuiTypography>
                </SuiBox>
                <SuiBox mx={1.5}>
                  <Controller
                    name="negative_prompt"
                    control={control}
                    render={({ field }) => (
                      <SuiInput
                        {...field}
                        size="small"
                        multiline
                        maxRows={2}
                        minRows={2}
                        placeholder="What do you want to avoid?"
                      />
                    )}
                  />{" "}
                </SuiBox>
              </SuiBox>
              <SuiBox
                pt={3}
                px={3}
                display="flex"
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                      Save
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </DialogContent>
        </form>
      </Dialog>
    );
  }
);

export default AdvancedSettingModal;
